export enum RoomType {
  // Chambres & rangements
  BEDROOM = 'BEDROOM',
  MASTER_BEDROOM = 'MASTER_BEDROOM',
  DORMITORY = 'DORMITORY',
  STUDIO_APARTMENT = 'STUDIO_APARTMENT',
  DRESSING_ROOM = 'DRESSING_ROOM',
  STORAGE_ROOM = 'STORAGE_ROOM',

  // Pièces de vie
  LIVING_ROOM = 'LIVING_ROOM',
  DINING_ROOM = 'DINING_ROOM',
  OFFICE = 'OFFICE',
  LIBRARY = 'LIBRARY',
  PLAYROOM = 'PLAYROOM',
  GYM = 'GYM',
  HOME_CINEMA = 'HOME_CINEMA',
  VERANDA = 'VERANDA',
  RECEPTION_ROOM = 'RECEPTION_ROOM',

  // Cuisine & buanderie
  KITCHEN = 'KITCHEN',
  PANTRY = 'PANTRY',
  LAUNDRY = 'LAUNDRY',
  LARDER = 'LARDER',

  // Sanitaires
  BATHROOM = 'BATHROOM',
  SHOWER_ROOM = 'SHOWER_ROOM',
  POWDER_ROOM = 'POWDER_ROOM',
  TOILET = 'TOILET',

  // Circulation & volumes
  ENTRANCE = 'ENTRANCE',
  HALLWAY = 'HALLWAY',
  LANDING = 'LANDING',
  STAIRWELL = 'STAIRWELL',
  MEZZANINE = 'MEZZANINE',
  ATTIC = 'ATTIC',
  BASEMENT = 'BASEMENT',
  CELLAR = 'CELLAR',
  WINE_CELLAR = 'WINE_CELLAR',
  BOILER_ROOM = 'BOILER_ROOM',

  // Extérieur
  OUTDOOR = 'OUTDOOR',
  GARDEN = 'GARDEN',
  TERRACE = 'TERRACE',
  BALCONY = 'BALCONY',
  LOGGIA = 'LOGGIA',
  COURTYARD = 'COURTYARD',
  POOL = 'POOL',
  POOL_HOUSE = 'POOL_HOUSE',

  // Dépendances & stationnement
  GARAGE = 'GARAGE',
  CARPORT = 'CARPORT',
  PARKING_SPACE = 'PARKING_SPACE',
  WORKSHOP = 'WORKSHOP',
  SHED = 'SHED',
  OUTBUILDING = 'OUTBUILDING',
  BARN = 'BARN',
  STABLE = 'STABLE',

  // Parties communes (immeuble)
  COMMON_AREA = 'COMMON_AREA',
  LOBBY = 'LOBBY',
  BIKE_ROOM = 'BIKE_ROOM',
  BIN_ROOM = 'BIN_ROOM',

  OTHER = 'OTHER',
}

/** Équipements sanitaires présents dans une pièce, indépendants de son type. */
export enum RoomFeature {
  BATHTUB = 'BATHTUB',
  SHOWER = 'SHOWER',
  WASHBASIN = 'WASHBASIN',
  DOUBLE_WASHBASIN = 'DOUBLE_WASHBASIN',
  TOILET = 'TOILET',
  HAND_BASIN = 'HAND_BASIN',
  BIDET = 'BIDET',
}
export enum BedType {
  SINGLE = 'SINGLE',
  DOUBLE = 'DOUBLE',
  QUEEN = 'QUEEN',
  KING = 'KING',
  BUNK = 'BUNK',
  SOFA_BED = 'SOFA_BED',
  BABY = 'BABY',
  COT = 'COT',
}

export enum BuildingType {
  MAIN_HOUSE = 'MAIN_HOUSE',
  APARTMENT = 'APARTMENT',
  ANNEX = 'ANNEX',
  GARAGE = 'GARAGE',
  POOL_HOUSE = 'POOL_HOUSE',
  OUTBUILDING = 'OUTBUILDING',
  LAND = 'LAND',
  OTHER = 'OTHER',
}

export interface BedDto {
  id: string;
  type: BedType;
  widthCm?: number;
  lengthCm?: number;
  label?: string;
  position: number;
  capacity: number;
  createdAt: string;
  updatedAt: string;
}

export interface FloorDto {
  id: string;
  name: string;
  level: number;
  position: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * Types de pièces qui portent un couchage — miroir de `SLEEPING_ROOM_TYPES` côté API. Seules
 * celles-là peuvent être attribuées à un séjour ; le back refuse les autres en 400.
 */
export const SLEEPING_ROOM_TYPES: RoomType[] = [
  RoomType.BEDROOM,
  RoomType.MASTER_BEDROOM,
  RoomType.DORMITORY,
  RoomType.MEZZANINE,
  RoomType.STUDIO_APARTMENT,
];

export const isSleepingRoomType = (type: RoomType): boolean => SLEEPING_ROOM_TYPES.includes(type);

export interface RoomDto {
  id: string;
  name: string;
  type: RoomType;
  features: RoomFeature[];
  /** Chambre desservie par cette pièce d'eau (« en suite »). */
  servedRoomId?: string | null;
  floorId?: string | null;
  areaSqm?: number;
  isOutOfService: boolean;
  position: number;
  capacity: number;
  beds: BedDto[];
  createdAt: string;
  updatedAt: string;
}

export interface BuildingDto {
  id: string;
  name: string;
  type?: BuildingType;
  position: number;
  capacity: number;
  floors: FloorDto[];
  rooms: RoomDto[];
  createdAt: string;
  updatedAt: string;
}

export interface CartographyTreeDto {
  buildings: BuildingDto[];
}

export interface CountByKeyDto {
  key: string;
  count: number;
}

export interface SleepingRoomsSummaryDto {
  total: number;
  withDoubleBed: number;
  withSingleBedsOnly: number;
  withoutBed: number;
  byFloor: CountByKeyDto[];
  byBuilding: CountByKeyDto[];
  byType: CountByKeyDto[];
}

export interface BedsSummaryDto {
  total: number;
  byType: CountByKeyDto[];
  byWidthCm: CountByKeyDto[];
}

export interface WashroomsSummaryDto {
  total: number;
  byType: CountByKeyDto[];
  byFeature: CountByKeyDto[];
  enSuite: number;
}

export interface CapacityByBuildingDto {
  buildingId: string;
  name: string;
  capacity: number;
}

export interface CartographySummaryDto {
  bedroomCount: number;
  bedCount: number;
  bathroomCount: number;
  totalCapacity: number;
  sleepingRooms: SleepingRoomsSummaryDto;
  beds: BedsSummaryDto;
  washrooms: WashroomsSummaryDto;
  capacityByBuilding: CapacityByBuildingDto[];
}

export interface CreateBuildingDto {
  name: string;
  type?: BuildingType;
  position?: number;
}

export type UpdateBuildingDto = Partial<CreateBuildingDto>;

export interface CreateFloorDto {
  name: string;
  level: number;
  position?: number;
}

export type UpdateFloorDto = Partial<CreateFloorDto>;

export interface CreateRoomDto {
  buildingId: string;
  floorId?: string;
  name: string;
  type: RoomType;
  features?: RoomFeature[];
  servedRoomId?: string | null;
  areaSqm?: number;
  isOutOfService?: boolean;
  position?: number;
}

export type UpdateRoomDto = Partial<CreateRoomDto>;

export interface CreateBedDto {
  type: BedType;
  widthCm?: number;
  lengthCm?: number;
  label?: string;
  position?: number;
}

export type UpdateBedDto = Partial<CreateBedDto>;
